import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import { useMediaQuery, useTheme } from '@mui/material';
import Navbar from '../../Components/Navbar/Navbar';
import Sidebar from '../../Components/Sidebar';
import '../../Styles/app.scss';
import {
  occupationsAtom,
  productsAtom,
  maxDistanceAtom,
  selectedCustomerIdAtom,
  selectedLeadIdAtom,
  additionalCustomerDataAtom,
  practiceProductAtom,
  alertDialogAtom,
  allOutcomesAtom,
  outcomesAtom,
  activityTypesAtom,
  activityOutcomesAtom,
  selectedBranchAtom,
} from '../../Atoms';
import { notifyCacheRefreshAtom } from '../../Atoms/HubAtoms';
import sortByProperty from '../../Helpers/Arrays/sortByProperty';
import useAPI from '../../api';
import parseValuesToBoolean from '../../Helpers/parseValuesToBoolean';
import keysToLowerCase from '../../Helpers/keysToLowerCase';
import useLocationName from '../../Hooks/useLocationName';
import useMobileMode from '../../Hooks/useMobileMode';
import { FullPageFlexBox, FullSizeColumn } from '../../Components/FlexBox';
import MobileShell from '../../Components/Mobile/MobileShell';
import MobileRouter from '../../Components/Mobile/MobileRouter';
import useHasPermission from '../../Hooks/useHasPermission';
import MainOutlet from './MainOutlet';
import { CREDITS_ALERT_SHOWN_FLAG } from '../../Utils/constants/general';

const Main = function Main({
  loggedUserId, //id
  onLogout,
  userRoles = null,
  selectedBranchId,
  routesTree = [],
}) {
  const API = useAPI();
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const locationName = useLocationName();

  /**Media queries**/
  const desktop = useMediaQuery('(min-width: 1300px)');
  const tablet = useMediaQuery((Theme) => Theme.breakpoints.up('md'));
  const heightQuery = useMediaQuery('(min-height:750px)');
  // Mobile layout switch (see MOBILE_PLAN.md §C): true at phone widths, below 600px.
  const mobileMode = useMobileMode();
  /****/

  const canViewPriorities = useHasPermission('activities.viewpriority');

  /**Atoms**/
  const setSectionOutcomes = useSetAtom(outcomesAtom);
  const setAllOutcomes = useSetAtom(allOutcomesAtom);
  const setAlertDialog = useSetAtom(alertDialogAtom);
  const notifyCacheRefresh = useAtomValue(notifyCacheRefreshAtom);

  const [selectedCustomerId, setSelectedCustomerId] = useAtom(
    selectedCustomerIdAtom
  );
  const [selectedBranch, setSelectedBranch] = useAtom(selectedBranchAtom);
  const setSelectedLeadId = useSetAtom(selectedLeadIdAtom);
  const setMaxDistance = useSetAtom(maxDistanceAtom);
  const additionalCustomerData = useAtomValue(additionalCustomerDataAtom);
  const [activityOutcomes, setActivityOutcomes] = useAtom(activityOutcomesAtom);
  const [occupations, setOccupations] = useAtom(occupationsAtom);
  const setProducts = useSetAtom(productsAtom);
  const [practiceProducts, setPracticeProducts] = useAtom(practiceProductAtom);
  const [activityTypes, setActivityTypes] = useAtom(activityTypesAtom);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [remainingSMS, setRemainingSMS] = useState(null);
  const [version, setVersion] = useState(null);
  const [callTargets, setCallTargets] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [prioritiesLoading, setPrioritiesLoading] = useState(true);
  const [userRedemptionData, setUserRedemptionData] = useState(null);

  /**Media queries match**/
  // Below 1300px the content needs the room: the sidebar starts as the rail.
  useEffect(() => {
    setSidebarOpen(desktop);
  }, [desktop]);

  // Stable, so the page's outlet context does not change when only the sidebar does.
  const handleChangeSection = useCallback(
    (page, extraParams) => {
      setSelectedCustomerId('');
      setSelectedLeadId('');
      navigate(page, { state: extraParams });
    },
    [navigate]
  );

  useEffect(() => {
    if (selectedBranchId) {
      API.getBranchSmtpEmail(selectedBranchId).then((res) => {
        setSelectedBranch({ ...selectedBranch, smtp: res?.data });
      });
    }
  }, [selectedBranchId]);

  useEffect(() => {
    if (activityOutcomes && locationName) {
      const sectionOutcomes =
        activityOutcomes.CALL?.filter(({ extensionData: { sections } }) =>
          sections?.find((x) =>
            additionalCustomerData?.outcomesFilter
              ? x === additionalCustomerData.outcomesFilter
              : x === locationName
          )
        ) || [];
      setSectionOutcomes(sectionOutcomes);
    }
  }, [locationName, activityOutcomes, additionalCustomerData?.outcomesFilter]);

  /**Max distance KM**/
  useEffect(() => {
    if (selectedBranchId) {
      API.getBranchMaxDistance(
        selectedBranchId,
        (res) => {
          if (res?.status === 204) setMaxDistance(0);
          else setMaxDistance(res);
        },
        () => {
          setMaxDistance(0);
        }
      );
    }
  }, [selectedBranchId]);
  /****/

  /**Branch data**/
  useEffect(() => {
    if (!selectedBranchId || selectedCustomerId) return;

    API.getCallTargets(
      selectedBranchId,
      (res) => {
        setCallTargets(res);
      },
      () => {}
    );
  }, [selectedBranchId, selectedCustomerId, locationName]);
  /****/

  /**Occupations**/
  useEffect(() => {
    if (
      (!occupations ||
        !Array.isArray(occupations) ||
        occupations.length === 0) &&
      loggedUserId
    ) {
      API.getOccupations(
        (result) => {
          setOccupations(result);
        },
        () => {
          setOccupations(null);
        }
      );
    }
  }, [loggedUserId]);
  /****/

  useEffect(() => {
    if (!loggedUserId) return;

    API.getUserRollingRedemption(
      (res) => {
        setUserRedemptionData(res);
      },
      () => {
        setUserRedemptionData(null);
      }
    );
  }, [loggedUserId]);

  /**Remaining SMS**/
  useEffect(() => {
    if (loggedUserId) {
      API.getCreditsSettings(
        ({ credits, alertThreshold, warningThreshold }) => {
          const smsNumber = !isNaN(credits) ? parseInt(credits, 10) : null;
          setRemainingSMS(smsNumber);
          if (sessionStorage.getItem(CREDITS_ALERT_SHOWN_FLAG)) return;
          if (credits < alertThreshold || credits < warningThreshold)
            sessionStorage.setItem(CREDITS_ALERT_SHOWN_FLAG, '1');
          if (credits < alertThreshold) {
            setAlertDialog({
              severity: 'error',
              title: intl.messages.ALERT,
              message: `${intl.messages.ALERTTHRESHOLD_MESSAGE}${credits}`,
            });
          } else if (credits < warningThreshold) {
            setAlertDialog({
              severity: 'warning',
              title: intl.messages.WARNING,
              message: `${intl.messages.WARNINGTHRESHOLD_MESSAGE}${credits}`,
            });
          }
        },
        () => {
          setRemainingSMS(null);
        }
      );
    }
  }, [loggedUserId]);
  /****/

  /**Products**/
  useEffect(() => {
    if (!selectedBranchId || !locationName) return;

    API.getAllProducts(
      selectedBranchId,
      locationName === 'MASSIVE_SENDING' ? 'CUSTOMERS' : locationName,
      (result) => {
        const sortedProducts = sortByProperty(
          result?.map(({ productName }) => productName),
          null,
          'ASCENDING'
        );
        setProducts(sortedProducts);
      },
      () => {
        setProducts(null);
      }
    );
  }, [selectedBranchId, locationName]);
  /****/

  /**Practice products**/
  useEffect(() => {
    if (
      (!practiceProducts ||
        !Array.isArray(practiceProducts) ||
        practiceProducts.length === 0) &&
      loggedUserId
    ) {
      API.getPracticeProducts(
        (result) => {
          setPracticeProducts(result?.sort((a, b) => b - a));
        },
        () => {
          setPracticeProducts(null);
        }
      );
    }
  }, [loggedUserId]);
  /****/

  const loadActivityOutcomes = (activityTypesArray) => {
    return API.getActivitiesOutcomes(
      (result) => {
        let groupedOutcomes = {};

        activityTypesArray.forEach((type) => {
          const typeOutcomes = result.filter(
            ({ activityTypeId, isActive }) =>
              isActive && activityTypeId === type?.id
          );
          const colors = theme.custom.generateConsistentUniqueColors(
            typeOutcomes.map(({ name }) => name)
          );
          groupedOutcomes = {
            ...groupedOutcomes,
            [[type?.name]]: typeOutcomes.map(
              ({ extensionData, ...data }, index) => ({
                ...data,
                color: colors[index],
                extensionData: keysToLowerCase(
                  parseValuesToBoolean(JSON.parse(extensionData))
                ),
              })
            ),
          };
        });

        setAllOutcomes(groupedOutcomes.CALL || []);
        setActivityOutcomes(groupedOutcomes);
      },
      () => {
        setActivityOutcomes([]);
      }
    );
  };

  useEffect(() => {
    API.getActivityTypes(
      (result) => {
        setActivityTypes([{ id: -1, name: 'MISSEDCALL' }, ...result]);
        loadActivityOutcomes(result);
      },
      () => {
        setActivityTypes([]);
      }
    );
  }, []);

  useEffect(() => {
    API.getCurrentVersion(
      (res) => {
        setVersion(res);
      },
      () => {}
    );
  }, []);

  const reloadActivityOutcomes = useCallback(() => {
    loadActivityOutcomes(activityTypes);
  }, [activityTypes]);

  // Not asked for without the key the endpoint enforces: the call is silent everywhere but the
  // priorities page, so a user who cannot read them was making a 403 on every branch change and
  // hearing nothing about it.
  useEffect(() => {
    if (!canViewPriorities) {
      // Settled rather than left pending: the screens reading this show a skeleton until it is
      // false, and there is no answer coming.
      setPrioritiesLoading(false);
      return;
    }
    if (!selectedBranchId) return;
    setPrioritiesLoading(true);
    API.getActivityPriority(
      selectedBranchId,
      { silent: locationName !== 'PRIORITIES' },
      (res) => {
        setPriorities(res);
        setPrioritiesLoading(false);
      },
      () => {
        setPriorities([]);
        setPrioritiesLoading(false);
      }
    );
  }, [selectedBranchId, notifyCacheRefresh, canViewPriorities]);

  // Mobile/tablet scaffold entry point (see MOBILE_PLAN.md §C/§D): the shell
  // carries its own nav and router, so nothing below here is reached under it.
  if (mobileMode) {
    return (
      <div style={{ display: loggedUserId ? 'block' : 'none' }}>
        <MobileShell>
          <MobileRouter
            priorities={priorities}
            prioritiesLoading={prioritiesLoading}
            userRoles={userRoles}
            onLogout={onLogout}
            version={version}
          />
        </MobileShell>
      </div>
    );
  }

  return (
    <div
      style={{
        display: loggedUserId ? 'block' : 'none',
      }}
    >
      <FullPageFlexBox>
        <Sidebar
          version={version}
          handleChangeSection={handleChangeSection}
          open={sidebarOpen}
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          routesTree={routesTree}
        />

        <FullSizeColumn sx={{ overflow: 'hidden', minWidth: 0 }}>
          <Navbar
            // eslint-disable-next-line no-undef
            title={APP_NAME}
            onLogout={onLogout}
            desktop={desktop}
            sidebarOpen={sidebarOpen}
            handleChangeSection={handleChangeSection}
            openSidebar={() => setSidebarOpen((prev) => !prev)}
            remainingSMS={remainingSMS}
            userRoles={userRoles}
            callTargets={callTargets}
            priorities={priorities}
            userRedemptionData={userRedemptionData}
            routesTree={routesTree}
          />
          <MainOutlet
            sidebarOpen={sidebarOpen}
            userRoles={userRoles}
            tablet={tablet}
            heightQuery={heightQuery}
            handleChangeSection={handleChangeSection}
            reloadActivityOutcomes={reloadActivityOutcomes}
            priorities={priorities}
          />
        </FullSizeColumn>
      </FullPageFlexBox>
    </div>
  );
};

export default Main;
